import { Badge, Box, Button, Grid, GridItem, Text } from "@chakra-ui/react";
import { AlertMessage, MovieCardDetailAction } from "@picovico/ui";
import { ProfileDrawerData, ProfileStatTabProps } from "./ProfileStatTabs";

import { useAppSelector } from "@/store";
import { formatShowDateTime, getCinemaHallTimeZone } from "@/utils/showtime";
import { convertTimeTo12HourFormat } from "@picovico/utils";
import { format } from "date-fns";
import { isArray } from "lodash";
import { useRouter } from "next/router";
import { getPageRoutes } from "routes/pageRoutes";
import { useGetUserReservationsQuery } from "store/services/user";
import { UserReservationDTO } from "types/index";

function TicketReservedCard({
  ticket,
  handleClick,
  movieCardDetailActionProps,
}: {
  ticket: UserReservationDTO;
  handleClick: (data: ProfileDrawerData) => void;
  movieCardDetailActionProps?: ProfileStatTabProps["movieCardDetailActionProps"];
}) {
  const router = useRouter();
  const config = useAppSelector((state) => state.config);
  const hallTimezone = getCinemaHallTimeZone(
    config.cinema_halls,
    ticket?.cinema_hall?.name,
  );
  const {
    cardHallScreenTextProps,
    cardDateShowTimeTextProps,
    cardSeatsTextProps,
    cardPaymentStatusBadgeProps,
  } = movieCardDetailActionProps || {};
  return (
    <>
      <MovieCardDetailAction
        {...(movieCardDetailActionProps || {})}
        onClick={() =>
          handleClick({
            image: ticket.poster || "",
            data: {
              movie: ticket.movie.name,
              cinema_hall: ticket.cinema_hall?.name,
              screen: ticket.screen,
              show_date:
                ticket.show_date && format(new Date(ticket.show_date), "PP"),
              booking_expiry:
                ticket.booking_expires_at &&
                (formatShowDateTime(
                  ticket.booking_expires_at,
                  hallTimezone,
                  "h:mm a",
                ) ??
                  convertTimeTo12HourFormat(
                    format(new Date(ticket?.booking_expires_at), "HH:mm"),
                  )),
              seats: ticket.seats,
              payment_status: (
                <Badge colorScheme="warning">{ticket.payment_status}</Badge>
              ),
            },
            actionButtons: (
              <Button
                w="full"
                colorScheme="primary"
                color={"primary.500"}
                variant="outline"
                onClick={() => {
                  router.push(
                    getPageRoutes(
                      "booking_confirmation",
                      {
                        movieIdx: ticket.movie.idx,
                        showIdx: ticket.show_idx,
                      },
                      {
                        booking_no: ticket.booking_no,
                        cinema_hall: ticket.cinema_hall?.idx,
                      },
                    ),
                  );
                }}
              >
                Purchase Ticket
              </Button>
            ),
          })
        }
        title={ticket?.movie.name}
        actionButton={
          <Box>
            <Button
              colorScheme="primary"
              color={"primary.500"}
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                router.push(
                  getPageRoutes(
                    "booking_confirmation",
                    {
                      movieIdx: ticket.movie.idx,
                      showIdx: ticket.show_idx,
                    },
                    {
                      booking_no: ticket.booking_no,
                      cinema_hall: ticket.cinema_hall?.idx,
                    },
                  ),
                );
              }}
            >
              Purchase Ticket
            </Button>
          </Box>
        }
        detail={
          <div>
            <Text
              fontSize="1rem"
              textTransform="capitalize"
              color="surface.100"
              {...cardHallScreenTextProps}
            >
              {ticket?.cinema_hall?.name}, {ticket?.screen}
            </Text>
            <Text
              textTransform="capitalize"
              fontSize="1rem"
              color="surface.100"
              {...cardDateShowTimeTextProps}
            >
              {ticket?.show_date && format(new Date(ticket?.show_date), "PP")}|{" "}
              {ticket?.start_time &&
                convertTimeTo12HourFormat(ticket?.start_time)}
            </Text>
            <Text
              textTransform="capitalize"
              fontSize="1rem"
              color="surface.100"
              {...cardSeatsTextProps}
            >
              {ticket?.seats}
            </Text>
            <Badge colorScheme="warning" {...cardPaymentStatusBadgeProps}>
              {ticket?.payment_status}
            </Badge>
          </div>
        }
        image={ticket?.poster || ""}
      />
    </>
  );
}

function TicketReserved({
  openDetail,
  movieCardDetailActionProps,
  alertMessageProps,
}: ProfileStatTabProps) {
  const { data, error, isError } = useGetUserReservationsQuery(
    {},
    { refetchOnMountOrArgChange: true },
  );

  return (
    <>
      {isError && (
        <AlertMessage
          w="full"
          message={error?.message || "Something went wrong"}
          {...alertMessageProps}
        />
      )}

      {data?.data?.length === 0 ? (
        <AlertMessage message="No reservation found" {...alertMessageProps} />
      ) : (
        <Grid
          gap="1rem"
          templateColumns={{
            base: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(2, 1fr)",
            // lg: "repeat(3, 1fr)",
          }}
        >
          {isArray(data?.data) &&
            data?.data?.map((ticketData, index) => (
              <GridItem key={`reserved_ticket_card_${index}`}>
                <TicketReservedCard
                  ticket={ticketData}
                  handleClick={openDetail}
                  movieCardDetailActionProps={movieCardDetailActionProps}
                />
              </GridItem>
            ))}
        </Grid>
      )}
    </>
  );
}

export default TicketReserved;
